// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { TimeRecordFilter } from '@/costs/api/get-time-records';
import { timeRecordTimeSeriesSchema } from '@/costs/api/types';
import { z } from 'zod';

export interface GetTimeRecordTimeSeriesParams {
  filter?: Omit<TimeRecordFilter, 'pageSize' | 'pageNumber' | 'sort'>;
  interval?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  groupBy?: 'worker' | 'project' | 'category';
}

/**
 * Response schema for time record time series API
 */
const timeRecordTimeSeriesResponseSchema = z.object({
  data: timeRecordTimeSeriesSchema,
  metadata: responseMetadataSchema,
});

/**
 * Fetches time record time series data from the API
 */
export const getTimeRecordTimeSeries = async (
  params: GetTimeRecordTimeSeriesParams = {}
): Promise<z.infer<typeof timeRecordTimeSeriesSchema>> => {
  const searchParams = new URLSearchParams();

  // Add filter parameters
  const filter = params.filter;
  if (filter) {
    // Add date range parameters
    if (filter.startDate) {
      searchParams.append('startDate', filter.startDate);
    }
    if (filter.endDate) {
      searchParams.append('endDate', filter.endDate);
    }

    // Add search parameter
    if (filter.search) {
      searchParams.append('search', filter.search);
    }

    // Add person IDs
    if (filter.personIds && filter.personIds.length > 0) {
      filter.personIds.forEach((id) => {
        searchParams.append('workerId', id);
      });
    }

    // Add project IDs
    if (filter.projectIds && filter.projectIds.length > 0) {
      filter.projectIds.forEach((id) => {
        searchParams.append('projectId', id);
      });
    }

    // Add categories
    if (filter.categories && filter.categories.length > 0) {
      filter.categories.forEach((cat) => {
        searchParams.append('category', cat);
      });
    }
  }

  // Add interval parameter (default to daily)
  searchParams.append('interval', params.interval || 'daily');

  // Add groupBy parameter if provided
  if (params.groupBy) {
    searchParams.append('groupBy', params.groupBy);
  }

  const url = `/v1/time-record-aggregate/granular?${searchParams.toString()}`;
  const response = await api.get(url);

  // Parse the full response and return just the data part
  const parsedResponse = await parseResponse(
    timeRecordTimeSeriesResponseSchema,
    response.data,
    {
      schemaName: 'TimeRecordTimeSeries',
      endpoint: url,
    }
  );
  return parsedResponse.data;
};
